/**
 * Registry of the WebGL renderers the game creates (GDD §15.7, issue #142). Every scene owns a renderer and so a
 * WebGL context; `dispose()` frees the GPU resources of the renderer but not the context, and a browser keeps
 * only about sixteen alive. Releasing a renderer here also forces the context loss, and the counters let the
 * tests check that every disposed scene gave its context back.
 */

export interface DisposableRenderer {
  dispose(): void;
  forceContextLoss(): void;
}

export interface RendererStats {
  created: number;
  released: number;
  /** Renderers still alive: created and not yet released. */
  live: number;
}

let created = 0;
let released = 0;
const live = new Set<DisposableRenderer>();

/** Tracks a renderer from the moment it is created; returns it for convenience. */
export function registerRenderer<T extends DisposableRenderer>(renderer: T): T {
  created += 1;
  live.add(renderer);
  return renderer;
}

/** Disposes a renderer and its WebGL context; releasing it twice does nothing the second time. */
export function releaseRenderer(renderer: DisposableRenderer): void {
  if (!live.delete(renderer)) return;
  released += 1;
  renderer.dispose();
  renderer.forceContextLoss();
}

export function rendererStats(): RendererStats {
  return { created, released, live: live.size };
}
